// Client-side prompt building utilities
// Moved from edge function to reduce server complexity

import { PromptTemplateService } from '@/services/promptTemplateService';

export interface PromptBuilderOptions {
  solutionName: string;
  generalInfo: string;
}

export interface BuiltPrompts {
  systemPrompt: string;
  userPrompt: string;
  solutionType: string;
  urls: string[];
}

// Helper function to extract URLs from text
export function extractUrls(text: string): string[] {
  const urlRegex = /https?:\/\/[^\s\n]+/g;
  return text.match(urlRegex) || [];
}

// Detect solution type based on URLs and context
export function detectSolutionType(urls: string[], context: string): string {
  const urlText = urls.join(' ').toLowerCase();
  const contextLower = context.toLowerCase();
  
  if (urlText.includes('arxiv.org') || contextLower.includes('paper') || contextLower.includes('research')) {
    return 'Research Paper';
  }
  if (urlText.includes('github.com') || contextLower.includes('open source')) {
    return 'Open Source Project';
  }
  if (contextLower.includes('api') || contextLower.includes('service')) {
    return 'API/Service';
  }
  if (contextLower.includes('tool') || contextLower.includes('platform')) {
    return 'Commercial Tool';
  }
  
  return 'AI Solution';
}

// Get specific guidelines for each solution type
export function getSolutionTypeGuidelines(solutionType: string): string {
  switch (solutionType) {
    case 'Research Paper':
      return `- Extract methodology and technical approach
- Identify key contributions and innovations  
- Focus on academic applications and research context
- Include publication details and citations
- Emphasize experimental results and benchmarks`;
      
    case 'Open Source Project':
      return `- Analyze GitHub repository activity and community metrics
- Extract technical specifications from documentation
- Focus on development status and release history
- Include community involvement and contribution guidelines
- Emphasize open source benefits and licensing`;
      
    case 'Commercial Tool':
      return `- Extract feature sets and pricing information
- Focus on user experience and interface capabilities
- Include business applications and ROI considerations
- Analyze customer testimonials and case studies
- Emphasize commercial benefits and support options`;
      
    case 'API/Service':
      return `- Extract endpoint documentation and usage limits
- Focus on integration capabilities and SDKs
- Include rate limits, pricing tiers, and authentication
- Analyze developer experience and documentation quality
- Emphasize technical implementation details`;
      
    default:
      return `- Extract core functionality and key features
- Focus on practical applications and use cases
- Include technical requirements and limitations
- Analyze user feedback and adoption metrics
- Emphasize unique value propositions`;
  }
}

// Build system prompt with high-quality example
export async function buildSystemPrompt(solutionType: string, existingTags: string[] = []): Promise<string> {
  return await PromptTemplateService.getSystemPrompt(solutionType, existingTags);
}


// Build user prompt based on solution type
export function buildUserPrompt(
  solutionName: string,
  urls: string[],
  generalInfo: string,
  solutionType: string
): string {
  return `Solution Name: ${solutionName}

${urls.length > 0 ? `URLs to Research:\n${urls.join('\n')}\n` : ''}

Context: ${generalInfo}

Task: Research this ${solutionType} solution thoroughly using the provided URLs and your knowledge. Extract comprehensive information including:

1. A CONCISE description (1-2 sentences) that clearly explains what the solution does
2. Detailed overview and technical description
3. Specific capabilities with descriptions and release information
4. Real-world use cases with implementation details
5. Known limitations and constraints
6. Actual version history with dates and status
7. Official website and documentation links
8. Development team or organization behind it

IMPORTANT: For the metadata.shortDescription field, provide a SHORT, CLEAR summary of what the solution does - NOT the raw input URLs or lengthy explanations.

DESCRIPTION RULE: Write the shortDescription as if explaining to someone who has never seen the research material - focus only on what ${solutionName} does, not what you learned from the sources.

Focus on accuracy and provide real, verifiable information. If the solution is academic (like ArXiv papers), emphasize research methodology, applications, and academic context. If it's a commercial tool, focus on features, pricing, and user experience.

Generate a comprehensive JSON response following the provided structure.`;
}

// Main function to build all prompts
export async function buildPrompts(options: PromptBuilderOptions & { existingTags?: string[] }): Promise<BuiltPrompts> {
  const { solutionName, generalInfo, existingTags = [] } = options;

  // Extract URLs and detect solution type
  const urls = extractUrls(generalInfo);
  const solutionType = detectSolutionType(urls, generalInfo);

  // Build prompts
  const systemPrompt = await buildSystemPrompt(solutionType, existingTags);
  const userPrompt = buildUserPrompt(solutionName, urls, generalInfo, solutionType);
  
  return {
    systemPrompt,
    userPrompt,
    solutionType,
    urls
  };
} 